"use client";

import React, { useState, useEffect, useCallback } from "react";
import Link from "next/link";
import { 
  Scale, 
  Briefcase, 
  Search, 
  Plus, 
  RotateCcw, 
  Calendar, 
  Gavel, 
  Building, 
  DollarSign, 
  AlertTriangle, 
  ShieldCheck, 
  CheckCircle2, 
  FileText,
  UserCheck,
  Columns,
  ListFilter
} from "lucide-react";
import { LegalCase } from "@/lib/demos/legalData";
import LegalKanban from "./LegalKanban";
import LegalDetailModal from "./LegalDetailModal";
import LegalStageModal from "./LegalStageModal";
import CaseEvaluationModal from "./CaseEvaluationModal";

export default function LegalCrmDashboard() {
  const [cases, setCases] = useState<LegalCase[]>([]);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState<"partner" | "associate" | "clerk">("partner");
  const [currency, setCurrency] = useState<"INR" | "USD">("INR");

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [stageFilter, setStageFilter] = useState("ALL");
  const [practiceAreaFilter, setPracticeAreaFilter] = useState("ALL");
  const [urgencyFilter, setUrgencyFilter] = useState("ALL");

  // Modals & Active Selections
  const [selectedCase, setSelectedCase] = useState<LegalCase | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isStageModalOpen, setIsStageModalOpen] = useState(false);
  const [targetStage, setTargetStage] = useState<LegalCase["stage"] | null>(null);
  const [isIntakeModalOpen, setIsIntakeModalOpen] = useState(false);

  // Toast
  const [notificationToast, setNotificationToast] = useState<{
    type: string;
    message: string;
  } | null>(null);

  const fetchCases = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.set("search", searchTerm);
      if (stageFilter !== "ALL") params.set("stage", stageFilter);
      if (practiceAreaFilter !== "ALL") params.set("practice_area", practiceAreaFilter);
      if (urgencyFilter !== "ALL") params.set("urgency", urgencyFilter);

      const res = await fetch(`/api/demos/legal/cases?${params.toString()}`);
      if (res.ok) {
        const data = await res.json();
        setCases(data.cases || []);
      }
    } catch (err) {
      console.error("Failed to load legal cases:", err);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, stageFilter, practiceAreaFilter, urgencyFilter]);

  useEffect(() => {
    fetchCases();
  }, [fetchCases]);

  const handleReset = async () => {
    if (confirm("Reset demo database to 8 benchmark litigation and corporate matters?")) {
      setLoading(true);
      await fetch("/api/demos/legal/reset", { method: "POST" });
      await fetchCases();
      setNotificationToast({
        type: "RESET",
        message: "Database successfully reset to initial benchmark matters.",
      });
      setTimeout(() => setNotificationToast(null), 3500);
    }
  };

  const handleSelectCase = (c: LegalCase) => {
    setSelectedCase(c);
    setIsDetailModalOpen(true);
  };

  const handleAdvanceStage = (c: LegalCase, nextStage: LegalCase["stage"]) => {
    setSelectedCase(c);
    setTargetStage(nextStage);
    setIsStageModalOpen(true);
  };

  const handleUpdateCase = (updated: LegalCase) => {
    setCases((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
    setSelectedCase(updated);
    setNotificationToast({
      type: "UPDATED",
      message: `Docket ${updated.case_number} updated to stage: ${updated.stage}`,
    });
    setTimeout(() => setNotificationToast(null), 3500);
  };

  // Metrics
  const activeMatters = cases.filter((c) => c.stage !== "Decree & Closed (Won)");
  const emergencyMatters = cases.filter((c) => c.urgency === "EMERGENCY");
  const inrRetainerTotal = cases.reduce((sum, c) => sum + (c.currency === "INR" ? (c.retainer_amount || 0) : 0), 0);
  const usdRetainerTotal = cases.reduce((sum, c) => sum + (c.currency === "USD" ? (c.retainer_amount || 0) : 0), 0);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col font-sans">
      
      {/* Top Bar Banner & Navigation */}
      <header className="sticky top-0 z-40 bg-slate-900/95 backdrop-blur-md border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex flex-col md:flex-row items-center justify-between gap-4">
          
          {/* Logo & Practice Name */}
          <div className="flex items-center gap-3">
            <Link href="/demos/legal" className="flex items-center gap-2.5 group">
              <div className="w-8 h-8 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-400 group-hover:scale-105 transition-transform">
                <Scale className="w-4 h-4" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-serif font-bold text-white text-base">LexJuris</span>
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400 border border-amber-500/30">
                    ATTORNEY CRM
                  </span>
                </div>
                <p className="text-[10px] text-slate-400">
                  Case Docket, Court Cause-List & Document Management
                </p>
              </div>
            </Link>
          </div>

          {/* Center: Role Switcher & Sandbox Controls */}
          <div className="flex flex-wrap items-center gap-2.5">
            {/* Role Switcher Pill */}
            <div className="flex items-center bg-slate-850 p-1 rounded-xl border border-slate-750 text-xs">
              <span className="text-[10px] font-bold text-slate-500 px-2">ROLE:</span>
              <button
                type="button"
                onClick={() => setUserRole("partner")}
                className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-all ${
                  userRole === "partner"
                    ? "bg-amber-500 text-slate-950 shadow-sm"
                    : "text-slate-400 hover:text-white"
                }`}
              >
                👑 Partner
              </button>
              <button
                type="button"
                onClick={() => setUserRole("associate")}
                className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-all ${
                  userRole === "associate"
                    ? "bg-amber-500 text-slate-950 shadow-sm"
                    : "text-slate-400 hover:text-white"
                }`}
              >
                ⚖️ Associate
              </button>
              <button
                type="button"
                onClick={() => setUserRole("clerk")}
                className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-all ${
                  userRole === "clerk"
                    ? "bg-amber-500 text-slate-950 shadow-sm"
                    : "text-slate-400 hover:text-white"
                }`}
              >
                📋 Clerk
              </button>
            </div>

            {/* Currency Toggle */}
            <div className="flex items-center bg-slate-850 p-1 rounded-xl border border-slate-750 text-xs">
              <button
                type="button"
                onClick={() => setCurrency("INR")}
                className={`px-2 py-1 rounded-lg text-[11px] font-bold ${
                  currency === "INR" ? "bg-amber-500 text-slate-950" : "text-slate-400"
                }`}
              >
                ₹ INR
              </button>
              <button
                type="button"
                onClick={() => setCurrency("USD")}
                className={`px-2 py-1 rounded-lg text-[11px] font-bold ${
                  currency === "USD" ? "bg-amber-500 text-slate-950" : "text-slate-400"
                }`}
              >
                $ USD
              </button>
            </div>
          </div>

          {/* Right: Actions */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleReset}
              title="Reset to 8 benchmark test matters"
              className="p-2 rounded-xl bg-slate-800 hover:bg-slate-750 text-slate-300 hover:text-white border border-slate-700 transition-colors"
            >
              <RotateCcw className="w-4 h-4" />
            </button>

            <button
              type="button"
              onClick={() => setIsIntakeModalOpen(true)}
              className="px-3.5 py-2 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-950 font-bold text-xs shadow-lg transition-all flex items-center gap-1.5"
            >
              <Plus className="w-4 h-4" />
              <span>New Matter Intake</span>
            </button>

            <Link
              href="/demos/legal"
              className="px-3 py-2 rounded-xl bg-slate-800 hover:bg-slate-750 text-slate-300 hover:text-white text-xs font-semibold border border-slate-700 transition-colors"
            >
              Law Firm Website
            </Link>
          </div>

        </div>
      </header>

      {/* Notification Toast */}
      {notificationToast && (
        <div className="bg-amber-500 text-slate-950 px-4 py-2 text-xs font-bold flex items-center justify-center gap-2 shadow-lg animate-fade-in">
          <CheckCircle2 className="w-4 h-4" />
          <span>{notificationToast.message}</span>
        </div>
      )}

      {/* Main Workspace Body */}
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        
        {/* Role Notice Indicator */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-2 p-2.5 rounded-xl bg-slate-900 border border-slate-800 text-xs text-slate-300">
          <div className="flex items-center gap-2">
            <UserCheck className="w-4 h-4 text-amber-400" />
            <span>
              Active Role View: <strong className="text-white uppercase">{userRole}</strong>
              {userRole === "partner" && " • Full Financial Ledger, Retainer Billing & Settlement Authority"}
              {userRole === "associate" && " • Case Briefs, Drafting Vault & Court Appearance Board"}
              {userRole === "clerk" && " • Cause List, Filing Diary Numbers & Order Sheet Uploads (Financials Hidden)"}
            </span>
          </div>
          <span className="text-[11px] text-slate-500">
            BCI Rule 36 Compliance Sandbox
          </span>
        </div>

        {/* 4 Metric Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="p-4 rounded-2xl bg-slate-900/80 border border-slate-800 space-y-1">
            <div className="flex items-center justify-between text-[11px] font-semibold text-slate-400">
              <span>Active Case Dockets</span>
              <Scale className="w-4 h-4 text-amber-400" />
            </div>
            <div className="text-2xl font-serif font-bold text-white">
              {activeMatters.length} Matters
            </div>
            <p className="text-[11px] text-slate-500">High Court, SC, NCLT & ADR</p>
          </div>

          <div className="p-4 rounded-2xl bg-slate-900/80 border border-slate-800 space-y-1">
            <div className="flex items-center justify-between text-[11px] font-semibold text-slate-400">
              <span>Emergency Hearings</span>
              <AlertTriangle className="w-4 h-4 text-rose-400" />
            </div>
            <div className="text-2xl font-serif font-bold text-rose-400">
              {emergencyMatters.length} Urgent
            </div>
            <p className="text-[11px] text-slate-500">Bail applications & stay prayers</p>
          </div>

          {userRole !== "clerk" ? (
            <div className="p-4 rounded-2xl bg-slate-900/80 border border-slate-800 space-y-1">
              <div className="flex items-center justify-between text-[11px] font-semibold text-slate-400">
                <span>Retainer Accounts Held</span>
                <DollarSign className="w-4 h-4 text-emerald-400" />
              </div>
              <div className="text-2xl font-bold font-mono text-emerald-400">
                {currency === "USD" ? `$${usdRetainerTotal.toLocaleString()}` : `₹${inrRetainerTotal.toLocaleString("en-IN")}`}
              </div>
              <p className="text-[11px] text-slate-500">Client trust advance ledger</p>
            </div>
          ) : (
            <div className="p-4 rounded-2xl bg-slate-900/80 border border-slate-800 space-y-1">
              <div className="flex items-center justify-between text-[11px] font-semibold text-slate-400">
                <span>Filing Diary Registry</span>
                <FileText className="w-4 h-4 text-blue-400" />
              </div>
              <div className="text-2xl font-serif font-bold text-white">
                {cases.length} Filed
              </div>
              <p className="text-[11px] text-slate-500">All scrutinies cleared</p>
            </div>
          )}

          <div className="p-4 rounded-2xl bg-slate-900/80 border border-slate-800 space-y-1">
            <div className="flex items-center justify-between text-[11px] font-semibold text-slate-400">
              <span>Next Board Date</span>
              <Calendar className="w-4 h-4 text-cyan-400" />
            </div>
            <div className="text-2xl font-bold font-mono text-white">
              Listed 24h
            </div>
            <p className="text-[11px] text-slate-500">4 appearances on daily cause list</p>
          </div>
        </div>

        {/* Search & Filter Bar */}
        <div className="p-4 rounded-2xl bg-slate-900/90 border border-slate-800 space-y-3 shadow-sm">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {/* Search Input */}
            <div className="relative">
              <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                <Search className="w-4 h-4" />
              </span>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search Title, CNR, Client, Opposing..."
                className="w-full pl-9 pr-4 py-2 bg-slate-850 border border-slate-750 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              />
            </div>

            {/* Stage Filter */}
            <div>
              <select
                value={stageFilter}
                onChange={(e) => setStageFilter(e.target.value)}
                className="w-full px-3 py-2 bg-slate-850 border border-slate-750 rounded-xl text-xs font-semibold text-slate-200 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              >
                <option value="ALL">All 6 Pipeline Stages</option>
                <option value="New Case Intake">1. New Case Intake</option>
                <option value="Consultation & Retainer">2. Consultation & Retainer</option>
                <option value="Drafting & Evidence">3. Drafting & Evidence</option>
                <option value="Filed in Court / Listed">4. Filed in Court / Listed</option>
                <option value="Hearing & Arguments">5. Hearing & Arguments</option>
                <option value="Decree & Closed (Won)">6. Decree & Closed (Won)</option>
              </select>
            </div>

            {/* Practice Area Filter */}
            <div>
              <select
                value={practiceAreaFilter}
                onChange={(e) => setPracticeAreaFilter(e.target.value)}
                className="w-full px-3 py-2 bg-slate-850 border border-slate-750 rounded-xl text-xs font-semibold text-slate-200 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              >
                <option value="ALL">All Practice Domains</option>
                <option value="Corporate & M&A">Corporate & M&A</option>
                <option value="Criminal Defense & Bail">Criminal Defense & Bail</option>
                <option value="Civil Litigation & Property">Civil Litigation & Property</option>
                <option value="Intellectual Property & Patents">Intellectual Property & Patents</option>
                <option value="Arbitration & ADR">Arbitration & ADR</option>
                <option value="Family Law & Trusts">Family Law & Trusts</option>
              </select>
            </div>

            {/* Urgency Filter */}
            <div>
              <select
                value={urgencyFilter}
                onChange={(e) => setUrgencyFilter(e.target.value)}
                className="w-full px-3 py-2 bg-slate-850 border border-slate-750 rounded-xl text-xs font-semibold text-slate-200 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              >
                <option value="ALL">All Urgencies</option>
                <option value="EMERGENCY">🚨 Emergency Only</option>
                <option value="HIGH">⚡ High Priority Only</option>
                <option value="NORMAL">Normal Priority</option>
              </select>
            </div>
          </div>
        </div>

        {/* Kanban Board Container */}
        {loading ? (
          <div className="h-64 flex items-center justify-center text-slate-400 text-xs">
            Loading legal matter dockets...
          </div>
        ) : (
          <LegalKanban
            cases={cases}
            userRole={userRole}
            onSelectCase={handleSelectCase}
            onAdvanceStage={handleAdvanceStage}
            onOpenNotification={(c) => {
              setSelectedCase(c);
              setIsDetailModalOpen(true);
            }}
          />
        )}

      </main>

      {/* Case Binder & Document Vault Modal */}
      <LegalDetailModal
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        caseItem={selectedCase}
        userRole={userRole}
        onUpdateCase={handleUpdateCase}
        onTriggerNotification={(type, msg) => {
          setNotificationToast({ type, message: msg });
          setTimeout(() => setNotificationToast(null), 4000);
        }}
      />

      {/* Stage Advance Modal */}
      <LegalStageModal
        isOpen={isStageModalOpen}
        onClose={() => setIsStageModalOpen(false)}
        caseItem={selectedCase}
        targetStage={targetStage}
        onUpdateCase={handleUpdateCase}
      />

      {/* New Case Intake Modal */}
      <CaseEvaluationModal
        isOpen={isIntakeModalOpen}
        onClose={() => setIsIntakeModalOpen(false)}
        currency={currency}
        onCaseCreated={(newCase) => {
          setCases((prev) => [newCase, ...prev]);
          setNotificationToast({
            type: "CREATED",
            message: `New case docket ${newCase.case_number} registered!`,
          });
          setTimeout(() => setNotificationToast(null), 3500);
        }}
      />

    </div>
  );
}
